import api from "@core/plugins/api";
import { PaymentDTO } from "@core/dto";
import { PAYMENT_RESPONSE } from "@core/constants";
import { StripeHelpers } from "@core/helpers";
import { StripeService } from "./stripe.service";

const isIbanValidUrl = (iban) => `/api/et/iban-validation/${iban}`;
const addCardUrl = () => `/api/et/add-card`;
const confirmPaymentUrl = (demandId) => `/api/et/demande/confirm-payment/${demandId}`;
const getCardsUrl = () => `/api/et/cards`;
const initCardAddingUrl = () => `/api/et/init-card-adding`;
const paymentWithCreditUrl = () => `/api/et/paiement/avoir`;
const preparePaymentUrl = (demandId) => `/api/et/demande/prepare-payment/${demandId}`;
const removeCardUrl = () => `/api/et/remove-card`;
const selectFavoriteUrl = () => `/api/et/paiement/favori`;
const unlockWithSubscriptionUrl = () => `/api/et/paiement/avoir`;
const getClientSecretKeyUrl = () => `/et/billing/trigger-mandate-registration`;

export class PaymentService {
  /**
   * Get etab iban from API
   * @returns {*} iban object
   * @memberof PaymentService
   */
  static async getClientSecretKey() {
    try {
      const { data } = await api.private.post(getClientSecretKeyUrl());
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Validate iban field
   * @param {*} { iban }
   * @returns {HTTPResponse}
   */
  static async isIbanValid({ iban }) {
    try {
      await api.private.get(isIbanValidUrl(iban));
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Initialize Stripe token from API
   * @returns card token
   * @memberof PaymentService
   */
  static async initNewCardToken() {
    try {
      const response = await api.private.get(initCardAddingUrl());
      return response && response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a new card from Stripe token
   * @param {String} { paymentId }
   * @memberof PaymentService
   */
  static async addNewCard({ paymentId }) {
    try {
      return api.private.post(addCardUrl(), { payment_id: paymentId });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Handle payment and bank authentication through Stripe
   * @param {*} { demandId, paymentToken, type, extraId, upfront = 0 }
   * @returns paymentResponse
   */
  static async handlePayment({ demandId, paymentToken, type, extraId, upfront = 0 }) {
    try {
      const paymentResponse = await PaymentService.preparePayment({ demandId, paymentToken, type, extraId, upfront });

      // if the bank asks for an authentication, we proceed
      if (paymentResponse.status === PAYMENT_RESPONSE.REQUIRED_ACTION) {
        const { error, paymentIntent } = await StripeService.getPaymentIntent(paymentResponse.client_secret);
        // there's a problem with the 3DSecure Auth, notify the front
        StripeHelpers.errorHandler({error});

        // there's no problem, let's pay
        return PaymentService.confirmPayment({ demandId, paymentId: paymentIntent.id, type, extraId, upfront });
      }

      // if the bank doesn't ask for authentication, the preparePayment method will proceed with payment by itself
      return paymentResponse;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Prepare payment from Stripe API, looking for info about wether or not we need bank authentication
   * @param {*} { demandId, paymentToken, type, extraId, upfront }
   * @returns paymentResponse
   */
  static async preparePayment({ demandId, paymentToken, type, extraId, upfront }) {
    try {
      const dto = new PaymentDTO({ paymentToken, type, extraId, upfront });
      const data = dto.preparePaymentData();
      const response = await api.private.post(preparePaymentUrl(demandId), data);
      return response && response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete card from Stripe id
   * @param {String} { cardId }
   * @memberof PaymentService
   */
  static async deleteCard({ cardId }) {
    try {
      const response = await api.private.delete(removeCardUrl(), { data: { card_id: cardId } });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Confirm payment after bank authentication
   * @param {*} { demandId, paymentId, type, extraId, upfront }
   * @returns
   */
  static async confirmPayment({ demandId, paymentId, type, extraId, upfront }) {
    try {
      const dto = new PaymentDTO({ paymentId, type, extraId, upfront });
      const data = dto.prepareConfirmPaymentData();
      const response = await api.private.post(confirmPaymentUrl(demandId), data);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all cards from API
   * @returns {*} cards collection
   * @memberof PaymentService
   */
  static async getCards() {
    try {
      const response = await api.private.get(getCardsUrl());
      return response && response.data && response.data.cards;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Pay with credits and select extra
   * @param {*} { demandId, extraId }
   * @returns {HTTPResponse}
   */
  static async payWithCredits({ demandId, extraId }) {
    try {
      const dto = new PaymentDTO({ demandId, extraId });
      const data = dto.preparePaymentWithCreditData();
      return api.private.post(paymentWithCreditUrl(), data);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Pay with credits and select extra
   * @param {*} { demandId, extraId }
   * @returns {HTTPResponse}
   */
  static async unlockWithSubscription({ demandId, extraId }) {
    try {
      const dto = new PaymentDTO({ demandId, extraId });
      const data = dto.prepareSelectWithoutCardData();
      return api.private.post(unlockWithSubscriptionUrl(), data);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Select extra without paying
   * @param {*} { demandId, extraId }
   * @returns {HTTPResponse}
   */
  static async selectFavorite({ demandId, extraId }) {
    try {
      const dto = new PaymentDTO({ demandId, extraId });
      const data = dto.prepareSelectWithoutCardData();
      return api.private.post(selectFavoriteUrl(), data);
    } catch (error) {
      throw error;
    }
  }
}
